import { withErrorBoundary } from "@sentry/react";
import moment from "moment";
import { useContext } from "react";
import { MeetingViewMeetingNodeNewPageFragmentFragment } from "types/graphql-schema";

import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Error from "@components/error/error";
import AppLink from "@components/link/link";
import { classNames } from "@helpers/css";
import { getUrl, removeCurrentYear } from "@helpers/helpers";

import DetectedOneononeBanner from "./detected-oneonone-banner";
import MeetingPageHeader from "./header";
import { MeetingPageContext } from "./meeting-page-context";
import MeetingFinalizeButton from "./meeting-sidebar/meeting-finalize";
import { MeetingVideoConferenceImage } from "./meeting/video-conference-button";
import { getMeetingVideoConferenceName } from "./meeting/video-conference-helpers";
import PreviousMeetingsPopoverPanel from "./previous-meetings-popover-panel";
import PublishMeetingButton from "./publish-meeting-button";
import MeetingConvertToReview1on1BannerWithSentry from "./review/convert-to-review-1-on-1-banner";

const MeetingDetails = ({
  meeting,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
}) => {
  const { isInExtension } = useContext(MeetingPageContext);
  const meetingGroup = meeting.meetingGroup;

  if (!meetingGroup) return null;

  const meetingDate = meeting.startDatetime
    ? removeCurrentYear(moment(meeting.startDatetime).format("ddd, ll"))
    : "Unscheduled";

  return (
    <>
      <DetectedOneononeBanner meeting={meeting} />

      {/* Stick the meeting header (date, title, action buttons) to the top of
          the scroll container so it stays visible while reading topics. The
          containing block is the per-meeting wrapper (e.g. OneononeRow on the
          1-on-1 tab), so the sticky header scrolls away with its meeting and
          the next meeting's header takes over. The negative top values cancel
          out Layout.Main's top padding (py-6 / md:py-10) so the header pins
          flush against Layout.Header instead of leaving a visible gap. */}
      <div
        className={classNames(
          "flex gap-4 items-start justify-between mb-6 py-4",
          !isInExtension && "sticky -top-6 md:-top-10 z-10 bg-[#FCFCFD]",
        )}
      >
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-1.5">
            <AppLink
              to={getUrl({
                meetingGroupId: meeting.meetingGroupId,
                meetingId: meeting.id,
              })}
              className="tf-header-xl shrink-0 no-underline hover:no-underline"
            >
              {meetingDate}
            </AppLink>
            {meeting.draft && (
              // 8px from the date (gap-1 + ml-1) — same-size header
              // text but a regular weight in gray-400 to read as a
              // subtler "Draft" annotation.
              <div className="tf-header-xl font-normal text-gray-400 ml-1 shrink-0">
                Draft
              </div>
            )}
            {meeting.isRecurring && (
              <PreviousMeetingsPopoverPanel meeting={meeting} hideDateButton />
            )}
          </div>
        </div>
        <div className="flex gap-1.5 items-center">
          {!isInExtension && meeting.videoConferenceUrl && (
            <Button
              className="shrink-0 px-0 w-[24px]"
              tooltip={`Join ${getMeetingVideoConferenceName({
                meeting,
                compact: false,
              })}`}
              url={meeting.videoConferenceUrl}
              target="_blank"
              size={ButtonSize.medium}
              theme={buttonTheme.text}
            >
              <MeetingVideoConferenceImage
                meeting={meeting}
                className="h-5 w-5"
              />
            </Button>
          )}
          {!isInExtension && (
            <MeetingPageHeader meetingGroup={meetingGroup} meeting={meeting} />
          )}
          <div className="hidden @2xl/layout-main:contents">
            <MeetingFinalizeButton
              meeting={meeting}
              buttonProps={{ size: ButtonSize.medium }}
            />
          </div>
          {meeting.draft && (
            <PublishMeetingButton
              meetingId={meeting.id}
              meetingGroupId={meeting.meetingGroupId}
              buttonProps={{
                size: ButtonSize.medium,
                theme: buttonTheme.primary,
              }}
            >
              Publish Meeting
            </PublishMeetingButton>
          )}
        </div>
      </div>

      <div
        className={classNames(
          "flex flex-col gap-4 empty:hidden text-sm text-gray-900",
          !isInExtension && "pb-2",
        )}
      >
        {meeting.meetingGroup.isFormalOneonone &&
          meeting.canUpdate?.permission &&
          meeting.assessmentDeliveriesMatching1on1Template.edges.length ===
            0 && (
            <MeetingConvertToReview1on1BannerWithSentry meeting={meeting} />
          )}
      </div>
    </>
  );
};

const MeetingDetailsWithSentry = withErrorBoundary(MeetingDetails, {
  fallback: <Error description={"The meeting could not be rendered."} />,
});

export default MeetingDetailsWithSentry;
